import { createSignal, For, type JSX, onMount, Show } from "solid-js";
import { Ghost } from "./Ghost";
import "./Shimmer.css";

type ShimmerProps<T = Record<string, unknown>> = {
	children: (props: T) => JSX.Element;
	mockData?: T;
	count?: number;
};

type MeasuredElement = {
	x: number;
	y: number;
	width: number;
	height: number;
	rounded?: boolean;
};

/**
 * Automatic skeleton loading state that measures child layout and overlays ghosts.
 *
 * [Design System →](http://localhost:5174/component/Shimmer)
 */
export const Shimmer = <
	T extends Record<string, unknown> = Record<string, unknown>,
>(
	props: ShimmerProps<T>,
) => {
	const [elements, setElements] = createSignal<MeasuredElement[]>([]);
	const [measured, setMeasured] = createSignal(false);
	let containerRef: HTMLDivElement | undefined;
	let startMeasurement: (() => void) | undefined;
	let measurementScheduled = false;

	onMount(() => {
		let cancelled = false;
		let retryCount = 0;
		const maxRetries = 10;

		const runMeasurement = () => {
			if (cancelled || !containerRef || measured()) return;

			const measuredElements: MeasuredElement[] = [];
			const containerRect = containerRef.getBoundingClientRect();

			const pushGhost = (rect: DOMRect, computedStyle: CSSStyleDeclaration) => {
				const borderRadius = computedStyle.borderRadius;
				const isRounded =
					borderRadius &&
					(borderRadius.includes("50%") ||
						borderRadius.includes("9999px") ||
						parseInt(borderRadius, 10) >=
							Math.min(rect.width, rect.height) / 2);

				measuredElements.push({
					x: rect.left - containerRect.left,
					y: rect.top - containerRect.top,
					width: rect.width,
					height: rect.height,
					rounded: !!isRounded,
				});
			};

			// Recursively measure all visible elements
			const measureElement = (el: Element) => {
				const rect = el.getBoundingClientRect();
				const computedStyle = window.getComputedStyle(el);
				// Skip invisible elements
				if (
					rect.width === 0 ||
					rect.height === 0 ||
					computedStyle.display === "none"
				) {
					return;
				}
				const tagName = el.tagName.toLowerCase();

				// Fixed-size containers (e.g. stats card icon box) — measure as one block
				if (el.hasAttribute("data-shimmer-ghost")) {
					pushGhost(rect, computedStyle);
					return;
				}

				// Always recurse into children first
				if (el.children.length > 0) {
					Array.from(el.children).forEach(measureElement);
				}

				// Check if element should be represented as a ghost
				const isTextNode =
					el.childNodes.length === 1 &&
					el.childNodes[0].nodeType === Node.TEXT_NODE;
				const hasTextContent = (el.textContent?.trim().length ?? 0) > 0;
				const isLeafElement =
					isTextNode ||
					tagName === "img" ||
					tagName === "svg" ||
					tagName === "button" ||
					tagName === "input" ||
					tagName === "textarea" ||
					(hasTextContent && el.children.length === 0);

				if (isLeafElement) {
					pushGhost(rect, computedStyle);
				}
			};

			Array.from(containerRef.children).forEach(measureElement);

			if (measuredElements.length === 0 && retryCount < maxRetries) {
				retryCount += 1;
				requestAnimationFrame(runMeasurement);
				return;
			}

			setElements(measuredElements);
			setMeasured(true);
		};

		const scheduleMeasurement = () => {
			// Two frames so layout can settle after fonts/CSS apply.
			requestAnimationFrame(() => {
				requestAnimationFrame(runMeasurement);
			});
		};

		startMeasurement = () => {
			if (!containerRef || measured() || measurementScheduled) return;
			measurementScheduled = true;
			void document.fonts.ready.then(scheduleMeasurement);
		};

		startMeasurement();

		return () => {
			cancelled = true;
			startMeasurement = undefined;
		};
	});

	const count = props.count ?? 1;

	return (
		<>
		{/* Measurement phase - render child with mockData hidden */}
		<Show when={!measured()}>
			<div
				ref={(el) => {
					containerRef = el;
					startMeasurement?.();
				}}
				style={{
					position: "relative",
					visibility: "hidden",
					"pointer-events": "none",
				}}
			>
				{props.children((props.mockData ?? {}) as T)}
			</div>
		</Show>

			{/* Ghost skeleton phase - render child root with ghosts inside */}
			<Show when={measured()}>
				<For each={Array(count).fill(0)}>
					{() => (
						<div
							class="gvtui-shimmer-content"
							style={{
								position: "relative",
							}}
						>
							{props.children((props.mockData ?? {}) as T)}
							{/* Insert ghosts inside the child root */}
							<div
								style={{
									position: "absolute",
									top: "0",
									left: "0",
									width: "100%",
									height: "100%",
									"pointer-events": "none",
								}}
							>
								{elements().map((el) => (
									<div
										style={{
											position: "absolute",
											visibility: "visible",
											left: `${el.x}px`,
											top: `${el.y}px`,
										}}
									>
										<Ghost
											width={el.width}
											height={el.height}
											rounded={el.rounded}
										/>
									</div>
								))}
							</div>
						</div>
					)}
				</For>
			</Show>
		</>
	);
};
